import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { IconButton } from "@mui/material";
import { INVENTORY_ACTIVITY_TYPES } from "../data-handling/finance-inventory-constants";
import useFinanceInventory from "../data-handling/use-finance-inventory";
import BranchLabelFilter from "../../../components/reusable-ui/branch-label-filter";
import { filterInventoryBranchUsingLabel } from "../../../helpers/common-helper";
import BranchFilterComponent from "../../../components/reusable-ui/branch-filter";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { REPORTS_SUBTABS_MAP } from "../../../data/reports-constants";
import { GridFilterListIcon } from "@mui/x-data-grid";
import SearchInputComponent from "../../../components/reusable-ui/search-input";
import ActivityFilterModal from "./activity-filter-modal";
import { ACTIVITY_DROPDOWN_TYPES } from "../data-handling/finance-inventory-constants";
import { debounce } from "lodash";

function TableHeader() {
  const {
    activityType,
    setActivityType,
    resetPageData,
    setBranchIds,
    branchIds: selectedBranchIds,
    applyFilteredActivity,
  } = useFinanceInventory();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSearchMode = false;
  const {
    permittedInventoryBranches,
    inventoryBranchLabels,
    inventoryBranchLabelsCategory,
    inventoryBranchLabelsCategoryMap,
  } = useSelector((state: RootState) => ({
    permittedInventoryBranches:
      state.businessReducer.permittedInventoryBranches || [],
    inventoryBranchLabels:
      state.businessReducer?.businessData?.inventoryBranchLabels || [],
    inventoryBranchLabelsCategory:
      state.businessReducer?.businessData?.inventoryBranchLabelsCategory || [],
    inventoryBranchLabelsCategoryMap:
      state.businessReducer?.businessData?.inventoryBranchLabelsCategoryMap ||
      {},
  }));

  const [selectedBranchLabels, setSelectedBranchLabels] = useState<string[]>(
    []
  );
  const [filteredStores, setFilteredStores] = useState<any[]>([]);

  useEffect(() => {
    if (permittedInventoryBranches?.length) {
      setFilteredStores(permittedInventoryBranches);
      if (!selectedBranchIds || selectedBranchIds.length === 0) {
        const allBranchIds = permittedInventoryBranches.map((b: any) => b.mid);
        setBranchIds(allBranchIds);
      }
    }
  }, [permittedInventoryBranches]);

  const handleLabelChange = (labels: string[]) => {
    setSelectedBranchLabels(labels);

    let matchingStores;
    if (labels && labels.length > 0) {
      matchingStores = filterInventoryBranchUsingLabel(
        permittedInventoryBranches,
        labels,
        inventoryBranchLabelsCategoryMap
      );
    } else {
      matchingStores = permittedInventoryBranches; // If no labels selected, all are considered matching for initial selection
    }

    setFilteredStores(permittedInventoryBranches); // Always show all permitted stores
    const updatedSelectedBranchIds = matchingStores.map(
      (s: any) => s?.mid || s?.branchId || s?.id
    );
    setBranchIds(updatedSelectedBranchIds);
    resetPageData();
  };

  const getTextFromSelectedValue = (selectedVal: string) => {
    switch (selectedVal) {
      case INVENTORY_ACTIVITY_TYPES.goods_received_note:
        return "All Goods Received Notes";
      case INVENTORY_ACTIVITY_TYPES.transfer_ins:
        return "All Transfer Ins";
      case INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note:
        return "All Goods Received Notes - Approved";
      case INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins:
        return "All Transfer Ins - Approved";
      default:
        return "--";
    }
  };
  
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const onSearchInput = (searchStr: string) => {
    setSearchText(searchStr);
    debouncedSearch(searchStr);
  };

  const debouncedSearch = useCallback(
    debounce((searchStr) => {
      applyFilteredActivity?.({
        globalOrderSearch: searchStr,
      });
    }, 300),
    []
  );

  const onClearInput = (searchStr: string) => {
    onSearchInput("");
  };

  return (
    <div
      onClick={() => isOpen && setIsOpen(!isOpen)}
      className="flex items-center justify-between p-24"
    >
      {!isSearchMode && (
        <div>
          <div className="font-semibold text-xl">
            <span>{getTextFromSelectedValue(activityType)}</span>{" "}
            <IconButton
              onClick={() => setIsOpen(true)}
              className="bg-[#0074FB1A] h-20 w-20 relative"
            >
              <ArrowDropDownIcon className="w-[24px] text-[#0074FB]" />
              {isOpen && (
                <div className="absolute z-40 text-black text-sm mt-2 top-[105%] left-[105%] rounded-[4px] shadow-lg bg-white">
                  {ACTIVITY_DROPDOWN_TYPES.map((option, index) => (
                    <div
                      key={option.value}
                      className={`px-16 py-12 text-nowrap cursor-pointer text-left ${
                        activityType === option.value
                          ? "bg-[#E6F1FF] font-medium"
                          : "hover:bg-gray-100 font-400"
                      }`}
                      onClick={() => {
                        setActivityType?.(option.value);
                        resetPageData();
                      }}
                    >
                      {option.text}
                    </div>
                  ))}
                </div>
              )}
            </IconButton>{" "}
          </div>
          <div className="font-400 text-14 mt-16 flex items-center gap-12">
            <BranchLabelFilter
              branchLabels={inventoryBranchLabels}
              branchLabelsCategory={inventoryBranchLabelsCategory}
              branchLabelsCategoryMap={inventoryBranchLabelsCategoryMap}
              preSelectedLabels={selectedBranchLabels}
              onApplyCtaClick={handleLabelChange}
              selectAllLabels={false}
              parentClass="!ml-0 !mt-0 h-[36px] w-[250px]"
              customClass="!h-[36px]"
            />
            <BranchFilterComponent
              onApplyCtaClick={(selectedVal: any) => {
                setBranchIds(selectedVal);
                resetPageData();
              }}
              branchType={REPORTS_SUBTABS_MAP.inventory.tabId}
              preSelectedBranchIds={selectedBranchIds}
              branchDataList={filteredStores}
              parentClass={"w-[300px] ml-[0px]"}
              modalCustomClass={"custom-modal-class"}
              alignmentChangeRequired
            ></BranchFilterComponent>
          </div>
        </div>
      )}
      <div
        className={`flex items-center justify-end gap-15 ${
          isSearchMode ? "w-full" : ""
        }`}
      >
        {!isSearchMode && (
          <div
            className="border select-none cursor-pointer active:brightness-75 active:bg-slate-100 border-solid flex items-center text-13 px-12 py-10 border-[#E8E8E9] rounded-[8px]"
            onClick={handleOpenModal}
          >
            <GridFilterListIcon
              className="h-18 text-[#898989] w-18 mr-12"
              style={{}}
            />
            Filters
          </div>
        )}
        <SearchInputComponent
          placeholder={"Search by order number"}
          customClass={"text-15 !py-[12px]"}
          isParentControlled
          parentControlledValue={searchText}
          parentControlledOnchange={onSearchInput}
          elementWidthClass="min-w-[350px] w-full"
          autoFocus={false}
          onClear={onClearInput}
        />
      </div>
      <ActivityFilterModal open={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
}

export default TableHeader;